/* Kuori · «Color de tu iPhone» (David, 20/10/2026). Només visual: no toca la variant, el preu ni la cistella.
   - Fitxa (snippets/kuori-pdp, part 'buybox', element [data-kcam]): sota el selector de model, una fila de mostres
     rodones amb els colors del mòbil triat. En triar-ne una, a les fotos de la galeria que són de la plantilla d'aquell
     model (la foto de producte del model) es posa a sobre una capa transparent amb la càmera d'aquell color.
   - Targetes (snippets/kuori-product-card): si la foto de la targeta és de la plantilla del model recordat («Tu
     modelo», localStorage «kuori:model») i per a aquell model s'ha triat un color, s'hi posa la mateixa capa.
   - El color triat es recorda per model (localStorage «kuori:camcolor» = { "iphone-18-pro-max": "burgundy" }).
   Dades: assets/kuori-kcam.json (models → plantilla i colors; fotos antigues → plantilla), capes assets/kcam-*.webp.
   Plantilla d'una foto: l'etiqueta «#cameres_<etiqueta>» de l'alt, el nom «iphone-case-…-<etiqueta>.jpg», el de les
   fotos de variant «kuori-<disseny>-<model>.jpg» (la plantilla del model; 22/10/2026) o la llista de fotos antigues del
   JSON (comprovades al píxel). Si no se sap, la foto no porta capa. */
const COLOR_KEY = 'kuori:camcolor';
const MODEL_KEY = 'kuori:model';
const RX_NAME = /^iphone-case-.+-(17pm|17air|16e|16|15pm|15|samsung)(_[0-9a-f-]{36})?\.(jpe?g|png|webp)$/i;
const RX_KUORI = /^(kuori-.+?)(_[0-9a-f-]{36})?\.(jpe?g|png|webp)$/i;

const slug = (s) => (s || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const readColors = () => { try { return JSON.parse(localStorage.getItem(COLOR_KEY) || '{}') || {}; } catch (e) { return {}; } };
const saveColor = (model, key) => {
  try { const d = readColors(); d[model] = key; localStorage.setItem(COLOR_KEY, JSON.stringify(d)); } catch (e) { /* sense emmagatzematge */ }
};
const rememberedModel = () => { try { return localStorage.getItem(MODEL_KEY) || ''; } catch (e) { return ''; } };

const tag = document.querySelector('script[data-kcam-src]');
const SRC = tag?.dataset.kcamSrc;
const BASE = SRC ? SRC.replace(/[^/]*$/, '') : '';
let DATA = null;
const load = () => DATA || (DATA = fetch(SRC.startsWith('//') ? `https:${SRC}` : SRC).then((r) => (r.ok ? r.json() : null)).catch(() => null));

const templateOf = (img, data) => {
  const t = (img.getAttribute('alt') || '').match(/#cameres_([a-z0-9]+)/i);
  if (t && data.tags[t[1].toLowerCase()]) return data.tags[t[1].toLowerCase()];
  let name = (img.getAttribute('src') || '').split('?')[0].split('/').pop() || '';
  try { name = decodeURIComponent(name); } catch (e) { /* nom tal qual */ }
  const m = name.match(RX_NAME);
  if (m) return data.tags[m[1].toLowerCase()];
  // Foto de variant: el model és el final més llarg del nom que coincideix amb un model del JSON
  const k = name.match(RX_KUORI);
  if (k) {
    const base = k[1].toLowerCase();
    const key = Object.keys(data.models).filter((x) => base.endsWith(`-${x}`)).sort((a, b) => b.length - a.length)[0];
    if (key) return data.models[key].tpl;
  }
  return data.images[name] || '';
};

/* Capa: un <img> absolut germà de la foto, posat sobre la part de la foto que es veu (object-fit inclòs) */
const place = (img, layer) => {
  const host = layer.parentElement;
  if (!host || !img.isConnected) return;
  // Mides sense transformacions (el zoom de la targeta en passar-hi per sobre també s'aplica a la capa, per CSS)
  const W = img.offsetWidth;
  const H = img.offsetHeight;
  if (!W || !H || img.offsetParent !== host) return;
  const left0 = img.offsetLeft;
  const top0 = img.offsetTop;
  const fit = getComputedStyle(img).objectFit;
  const ratio = 3 / 4;
  let cw = W; let ch = H;
  if ((fit === 'contain' || fit === 'cover') && Math.abs(W / H - ratio) > 0.002) {
    const wide = W / H > ratio;
    if ((fit === 'contain') === wide) { ch = H; cw = H * ratio; } else { cw = W; ch = W / ratio; }
  }
  const ox = left0 + (W - cw) / 2;
  const oy = top0 + (H - ch) / 2;
  const c = layer._kcam;
  const l = ox + cw * c.x / 100;
  const t = oy + ch * c.y / 100;
  Object.assign(layer.style, {
    left: `${l}px`, top: `${t}px`,
    width: `${cw * c.w / 100}px`, height: `${ch * c.h / 100}px`,
    transformOrigin: `${left0 + W / 2 - l}px ${top0 + H / 2 - t}px`,
  });
};
const ro = 'ResizeObserver' in window ? new ResizeObserver((entries) => entries.forEach((e) => {
  const layer = e.target._kcamLayer;
  if (layer) place(e.target, layer);
})) : null;

/* La capa no es veu fins que la foto de la funda i la de la càmera han carregat, i llavors entra suau (26/10/2026: a
   les col·leccions la càmera sortia una fracció de segon abans que la foto). */
const loaded = (el) => el.complete && el.naturalWidth > 0;
const reveal = (img, layer) => {
  if (loaded(img) && loaded(layer)) { layer.style.opacity = '1'; return; }
  const again = () => { if (img._kcamLayer === layer) reveal(img, layer); };
  if (!loaded(img)) img.addEventListener('load', again, { once: true });
  if (!loaded(layer)) layer.addEventListener('load', again, { once: true });
};

const setLayer = (img, color) => {
  let layer = img._kcamLayer;
  if (!color || !color.asset) {
    if (layer) { layer.remove(); img._kcamLayer = null; ro?.unobserve(img); }
    return;
  }
  if (!layer || !layer.isConnected || layer.parentElement !== img.parentElement) {
    layer?.remove();
    layer = document.createElement('img');
    layer.className = 'kcam-layer';
    layer.alt = '';
    layer.setAttribute('aria-hidden', 'true');
    layer.decoding = 'async';
    layer.style.opacity = '0';
    layer.style.transition = 'opacity .25s ease';
    img.parentElement.classList.add('kcam-host');
    img.after(layer);
    img._kcamLayer = layer;
    ro?.observe(img);
  }
  layer._kcam = color;
  const url = BASE + color.asset;
  if (layer.getAttribute('src') !== url) {
    layer.style.opacity = '0';
    layer.setAttribute('src', url);
  }
  place(img, layer);
  reveal(img, layer);
};

/* ---------- Fitxa ---------- */
const section = () => document.querySelector('.shopify-section--main-product');
const checkedModel = () => {
  const vp = section()?.querySelector('variant-picker');
  const r = vp?.querySelector('input[type="radio"][data-option-position="1"]:checked');
  return (r?.nextElementSibling?.textContent || r?.value || '').trim();
};
const galleryImages = () => [...(section()?.querySelectorAll('product-gallery .product-gallery__media') || [])]
  .map((m) => ({ media: m, img: m.querySelector('img:not(.kcam-layer)') }))
  .filter((x) => x.img);

const swatchesSig = new WeakMap();
const renderPdp = (data) => {
  const box = document.querySelector('[data-kbox][data-mounted] [data-kcam]') || document.querySelector('[data-kcam]');
  const name = checkedModel();
  const model = data.models[slug(name)];
  const media = galleryImages();
  const matching = model ? media.filter((x) => !x.media.hidden && templateOf(x.img, data) === model.tpl) : [];
  // Mostres: només si el model té colors i hi ha una foto del producte d'aquest model
  const show = !!(box && model && model.colors.length > 1 && matching.length);
  if (box) box.hidden = !show;
  let color = null;
  if (show) {
    const stored = readColors()[slug(name)];
    color = model.colors.find((c) => c.key === stored) || model.colors.find((c) => !c.asset) || null;
    const list = box.querySelector('[data-kcam-list]');
    const sig = `${slug(name)}|${color?.key || ''}`;
    if (swatchesSig.get(list) !== sig) {
      swatchesSig.set(list, sig);
      const brand = /^iphone/i.test(name) ? 'iPhone' : (name.split(/\s+/)[0] || '');
      box.querySelector('[data-kcam-text]').textContent = (box.dataset.label || '').replace('__BRAND__', brand);
      box.querySelector('[data-kcam-name]').textContent = color ? color.name : '';
      const current = [...list.children].map((b) => b.dataset.key).join('|');
      if (current !== model.colors.map((c) => c.key).join('|')) {
        list.replaceChildren(...model.colors.map((c) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'kcam__dot';
          b.dataset.key = c.key;
          b.style.setProperty('--kcam-c', c.hex);
          b.setAttribute('aria-label', (box.dataset.swatch || 'Color __NAME__').replace('__NAME__', c.name));
          b.title = c.name;
          return b;
        }));
      }
      [...list.children].forEach((b) => b.setAttribute('aria-pressed', String(!!color && b.dataset.key === color.key)));
    }
  }
  // Capes: a les fotos visibles de la plantilla del model; a la resta, cap
  media.forEach((x) => setLayer(x.img, show && matching.includes(x) ? color : null));
};

const startPdp = (data) => {
  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; renderPdp(data); });
  };
  renderPdp(data);
  // Els canvis de les nostres capes i mostres no compten
  const ours = (n) => n.nodeType === 1 && (n.classList.contains('kcam-layer') || n.classList.contains('kcam__dot'));
  new MutationObserver((list) => {
    if (list.some((m) => (m.type === 'attributes' ? !ours(m.target) : ![...m.addedNodes, ...m.removedNodes].every(ours)))) queue();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'src'] });
  document.addEventListener('change', (e) => { if (e.target.matches?.('input[data-option-position="1"]')) queue(); });
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-kcam] .kcam__dot');
    if (!b) return;
    const name = checkedModel();
    if (!name) return;
    saveColor(slug(name), b.dataset.key);
    renderPdp(data);
  });
};

/* ---------- Targetes ---------- */
const renderCards = (data) => {
  const name = rememberedModel();
  const model = data.models[slug(name)];
  const key = model && readColors()[slug(name)];
  const color = key && model.colors.find((c) => c.key === key && c.asset);
  document.querySelectorAll('product-card .kpc__img--main').forEach((img) => {
    setLayer(img, color && templateOf(img, data) === model.tpl ? color : null);
  });
};
const startCards = (data) => {
  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; renderCards(data); });
  };
  renderCards(data);
  new MutationObserver((list) => {
    if (list.some((m) => m.type === 'attributes' ? m.target.classList?.contains('kpc__img--main')
      : [...m.addedNodes].some((n) => n.nodeType === 1 && !n.classList.contains('kcam-layer')))) queue();
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
  window.addEventListener('storage', (e) => { if (e.key === COLOR_KEY || e.key === MODEL_KEY) queue(); });
  document.addEventListener('click', (e) => { if (e.target.closest?.('[data-kmc-model]')) setTimeout(queue, 0); });
};

if (SRC) {
  load().then((data) => {
    if (!data) return;
    if (document.querySelector('[data-kcam]')) startPdp(data);
    if (document.querySelector('product-card')) startCards(data);
  });
}
